import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import merge from "lodash/merge"
import React, { useEffect, useRef, useState } from "react";
import { css } from "styled-components";
import parse from 'html-react-parser';

const enum fields {
    jsInjection = "jsInjection",
    content = "content",
    css = "css",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.jsInjection]: string;
        [fields.content]: string;
        [fields.css]: string;
    };
    extendedStyles?: TrustedContentStyles;
}

type Props = OwnProps & HasShellContext;

export interface TrustedContentStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    instructionText?: TypographyPresentationProps;
}

export const trustedContentStyles: TrustedContentStyles = {
    wrapper: {
        css: css`
            background: #e5e5e5;
            padding: 10px;
        `,
    },
    titleText: {
        variant: "h6",
        css: css`
            margin: 0;
        `,
    },
};

const TrustedContent = ({ fields, shellContext, extendedStyles, id }: Props) => {
    const [styles] = useState(() => merge(trustedContentStyles, extendedStyles));
    const wrapperRef = useRef<HTMLDivElement>(null);

    const userStyles = {
        css: css`
        #trustedContentContainer_${id}
        {
            ${fields.css}
        }`,
    }

    useEffect(() => {
        if (wrapperRef.current) {
            const fragment = document.createRange().createContextualFragment(fields.content);
            wrapperRef.current.innerHTML = "";
            wrapperRef.current.appendChild(fragment);
        }
    }, []);

    return (
        <>
            {shellContext.isInShell && shellContext.isEditing && (
                <StyledWrapper {...styles.wrapper}>
                    <Typography {...styles.titleText}>{translate("Trusted Content")}</Typography>
                    <Typography {...styles.instructionText}>{siteMessage("TrustedContent_Instruction")}</Typography>
                </StyledWrapper>
            )}
            <StyledWrapper {...userStyles}>
                {IS_SERVER_SIDE ? (
                    <div>
                        {parse(fields.content, {
                            replace: domNode =>
                                typeof domNode === "object" &&
                                domNode !== null &&
                                "name" in domNode &&
                                domNode.name === "script"
                                    ? false
                                    : undefined,
                        })}
                    </div>
                ) : (
                    <div id={`trustedContentContainer_${id}`} ref={wrapperRef}></div>
                )}
            </StyledWrapper>
        </>
    );
}

const widgetModule: WidgetModule = {
    component: withIsInShell(TrustedContent),
    definition: {
        group: "Basic",
        canAdd: context => context.permissions?.canUseAdvancedFeatures || false,
        fieldDefinitions: [
            {
                name: fields.content,
                displayName: "Html",
                tooltip: "",
                editorTemplate: "CodeSnippetField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.css,
                displayName: "Css",
                tooltip: "",
                editorTemplate: "CodeSnippetField",
                defaultValue: "",
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
